import { TFilterValues, TTableFilter } from '@components/table/types';
import { getQueryParamForAppliedFilter } from '@components/table/TableCreateFiltersModal/getQueryParamForAppliedFilter';

/**
 * Formats filters for deletion, including handling multi-select and dependent fields.
 * @param fieldKey - The key of the field to remove.
 * @param idToFind - The specific value to remove (for multi-select fields).
 * @param appliedFilters - The currently applied filters.
 * @param filters - The list of all filters.
 * @param searchParamsInTheUrl - The URLSearchParams object to update.
 * @returns An object containing the updated filters and the modified URLSearchParams.
 */
export const formatFiltersForDelete = (
  fieldKey: string,
  idToFind: string | undefined,
  appliedFilters: TFilterValues | undefined,
  filters: TTableFilter[] | undefined,
  searchParamsInTheUrl: URLSearchParams,
): { filtersToClear: TFilterValues; updatedSearchParams: URLSearchParams } => {
  const fieldsWatching = filters?.filter((f) => f.watchedField === fieldKey);

  let filtersToClear: TFilterValues = {};

  if (idToFind !== undefined) {
    // Handle multi-select fields
    const appliedValue = appliedFilters?.[fieldKey];
    if (Array.isArray(appliedValue)) {
      const newValue = [...appliedValue];

      const index = newValue.findIndex((x: any) => {
        if (typeof x === 'string') {
          return x === idToFind;
        } else {
          return x.value === idToFind;
        }
      });

      newValue.splice(index, 1);
      filtersToClear = {
        [fieldKey]: newValue.length > 0 ? newValue : undefined,
      };

      if (newValue.length === 0) {
        searchParamsInTheUrl.delete(fieldKey);
      } else {
        const queryParam = getQueryParamForAppliedFilter(fieldKey, newValue);
        if (queryParam !== null) {
          searchParamsInTheUrl.set(fieldKey, queryParam);
        }
      }
    }
  } else {
    // Handle single-value fields
    filtersToClear = {
      [fieldKey]: undefined,
    };

    searchParamsInTheUrl.delete(fieldKey);
  }

  // Clear out any fields watching this one
  fieldsWatching?.forEach((f) => {
    filtersToClear[f.field] = undefined;
    searchParamsInTheUrl.delete(f.field);
  });

  return { filtersToClear, updatedSearchParams: searchParamsInTheUrl };
};
